import { useEffect, useRef, useState } from "react";
import { useTheme } from "next-themes";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, PenTool, Type, ArrowLeft } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_TURF_HOUSE = "https://customer-assets.emergentagent.com/job_field-rental/artifacts/nltdiuae_turf%20blog.png";

const WAIVER_KINDS = {
  turf_house: {
    key: "turf_house",
    label: "Turf House",
    subtitle: "Required before participating in any activity at Turf House.",
    heading: "Release & Waiver of Liability",
    logo: LOGO_TURF_HOUSE,
    accent: "emerald",
  },
  rrdc_field: {
    key: "rrdc_field",
    label: "RRDC Field",
    subtitle: "Required before participating in any Rolling River / Riverview Athletics activity.",
    heading: "Release & Waiver of Liability",
    logo: null,
    accent: "sky",
  },
};

const SIGNER_TYPES = [
  { id: "organization", label: "For an Organization / Team" },
  { id: "self", label: "For Myself" },
  { id: "child", label: "For My Child" },
];

const EMPTY_FORM = {
  signer_type: "organization",
  organization: "",
  first_name: "",
  last_name: "",
  date_of_birth: "",
  parent_first_name: "",
  parent_last_name: "",
  child_first_name: "",
  child_last_name: "",
  child_dob: "",
  email: "",
  phone: "",
  address: "",
};

export default function PublicWaiverPage() {
  const { theme, setTheme } = useTheme();
  const [kind, setKind] = useState(null);  // null = landing screen; "turf_house" | "rrdc_field" = form
  const [form, setForm] = useState(EMPTY_FORM);
  const [sigMode, setSigMode] = useState("typed");
  const [typedName, setTypedName] = useState("");
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [legalBody, setLegalBody] = useState("");

  useEffect(() => {
    if (!kind) return;
    // Pull the actual waiver template text for the chosen kind so the
    // on-page legal copy matches the emailed / stored PDF word-for-word.
    axios.get(`${API_URL}/api/public/waiver-template?kind=${kind}`)
      .then(({ data }) => setLegalBody(data.body || ""))
      .catch(() => { /* page still works; legal block will just be hidden */ });
  }, [kind]);

  useEffect(() => {
    const prev = theme;
    setTheme("light");
    return () => { if (prev && prev !== "light") setTheme(prev); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (sigMode === "drawn" && canvasRef.current) {
      const c = canvasRef.current;
      c.width = c.offsetWidth;
      c.height = 160;
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.strokeStyle = "#18181b";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
    }
  }, [sigMode]);

  const startDraw = (e) => {
    const c = canvasRef.current; const ctx = c.getContext("2d");
    const rect = c.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.beginPath(); ctx.moveTo(x, y);
    setDrawing(true);
  };
  const draw = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const c = canvasRef.current; const ctx = c.getContext("2d");
    const rect = c.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.lineTo(x, y); ctx.stroke();
  };
  const stopDraw = () => setDrawing(false);
  const clearCanvas = () => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
  };

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async () => {
    let sigData = "";
    if (sigMode === "typed") {
      if (!typedName.trim()) { toast.error("Type your full name to sign"); return; }
      sigData = typedName;
    } else {
      sigData = canvasRef.current?.toDataURL("image/png") || "";
      if (!sigData || sigData.length < 1000) { toast.error("Please draw your signature"); return; }
    }
    if (!form.email.trim()) { toast.error("Email is required"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/public/waiver`, {
        ...form,
        kind,
        signature_type: sigMode,
        signature_data: sigData,
      });
      setDone(true);
      toast.success("Waiver signed — thank you!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-4">
        <Card className="w-full max-w-md text-center border-gray-200" data-testid="waiver-done">
          <CardContent className="p-8 space-y-3">
            <CheckCircle2 size={48} className="mx-auto text-emerald-500" />
            <h2 className="text-2xl font-bold text-gray-900">Waiver Signed</h2>
            <p className="text-gray-600">Thank you. A confirmation email with your signed copy is on its way.</p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
              <Button variant="outline" onClick={() => { setDone(false); setForm(EMPTY_FORM); setTypedName(""); clearCanvas(); }} data-testid="sign-another-btn">
                Sign Another (same waiver)
              </Button>
              <Button variant="ghost" onClick={() => { setDone(false); setKind(null); setForm(EMPTY_FORM); setTypedName(""); clearCanvas(); }} data-testid="pick-different-waiver-btn">
                Pick a different waiver
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Landing screen: two big buttons to pick which waiver to sign.
  if (!kind) {
    return (
      <div className="min-h-screen bg-white py-10 px-4 text-gray-900 flex items-center justify-center" data-testid="waiver-landing">
        <div className="max-w-2xl w-full space-y-8 text-center">
          <div>
            <h1 className="text-4xl font-bold tracking-tight">Sign a Waiver</h1>
            <p className="text-gray-500 mt-3">Which activity are you signing in for?</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => setKind("turf_house")}
              className="group relative overflow-hidden rounded-2xl border-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-500 transition-all p-8 text-left"
              data-testid="pick-turf-house-btn"
            >
              <img src={LOGO_TURF_HOUSE} alt="Turf House" className="h-14 w-auto mb-4" />
              <div className="text-xl font-bold text-emerald-900">Turf House</div>
              <div className="text-sm text-emerald-700/80 mt-1">Turf House sports &amp; activity waiver</div>
              <div className="mt-4 text-emerald-800 font-semibold text-sm">Sign this waiver →</div>
            </button>
            <button
              type="button"
              onClick={() => setKind("rrdc_field")}
              className="group relative overflow-hidden rounded-2xl border-2 border-sky-200 bg-sky-50 hover:bg-sky-100 hover:border-sky-500 transition-all p-8 text-left"
              data-testid="pick-rrdc-field-btn"
            >
              <div className="h-14 w-auto mb-4 flex items-start">
                <div className="text-2xl font-black text-sky-900 leading-tight tracking-tight">
                  RRDC<br />Field
                </div>
              </div>
              <div className="text-xl font-bold text-sky-900">RRDC Field</div>
              <div className="text-sm text-sky-700/80 mt-1">Rolling River / Riverview Athletics activity waiver</div>
              <div className="mt-4 text-sky-800 font-semibold text-sm">Sign this waiver →</div>
            </button>
          </div>
          <p className="text-xs text-gray-400 pt-4">
            Not sure which one? Ask a Turf House / Rolling River staff member.
          </p>
        </div>
      </div>
    );
  }

  const meta = WAIVER_KINDS[kind];

  return (
    <div className="min-h-screen bg-white py-8 px-4 text-gray-900" data-testid="public-waiver-page" data-waiver-kind={kind}>
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Back to picker */}
        <div>
          <button
            type="button"
            onClick={() => { setKind(null); setForm(EMPTY_FORM); setTypedName(""); clearCanvas(); }}
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900"
            data-testid="back-to-waiver-picker-btn"
          >
            <ArrowLeft size={14} /> Back
          </button>
        </div>
        {/* Brand header */}
        <div className="flex items-center justify-center pb-2 border-b border-gray-200">
          {meta.logo ? (
            <img src={meta.logo} alt={meta.label} className="h-16 w-auto" />
          ) : (
            <div className="text-2xl font-black text-sky-900 tracking-tight py-2">{meta.label}</div>
          )}
        </div>
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight">{meta.heading}</h1>
          <p className="text-gray-500 text-sm mt-2">{meta.subtitle}</p>
        </div>

        <Card className="border-gray-200">
          <CardContent className="p-6 space-y-5">
            {/* Signer type */}
            <div className="space-y-2">
              <label className="text-sm font-medium">I am signing</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {SIGNER_TYPES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => set("signer_type", t.id)}
                    className={`text-sm rounded-md border px-3 py-2 transition-colors ${form.signer_type === t.id ? "border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold" : "border-gray-300 hover:border-gray-400"}`}
                    data-testid={`signer-type-${t.id}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Mode-specific fields */}
            {form.signer_type === "organization" && (
              <div className="space-y-3">
                <Field label="Organization / Team Name *" testId="org-name">
                  <Input value={form.organization} onChange={(e) => set("organization", e.target.value)} data-testid="org-name-input" />
                </Field>
              </div>
            )}

            {form.signer_type === "self" && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="First Name *">
                    <Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="self-first-name" />
                  </Field>
                  <Field label="Last Name *">
                    <Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="self-last-name" />
                  </Field>
                </div>
                <Field label="Date of Birth">
                  <Input type="date" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} data-testid="self-dob" />
                </Field>
              </div>
            )}

            {form.signer_type === "child" && (
              <div className="space-y-3">
                <p className="text-xs text-gray-500 -mt-1">As parent or legal guardian, you are signing on behalf of your minor child.</p>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Parent / Guardian First Name *">
                    <Input value={form.parent_first_name} onChange={(e) => set("parent_first_name", e.target.value)} data-testid="parent-first-name" />
                  </Field>
                  <Field label="Parent / Guardian Last Name *">
                    <Input value={form.parent_last_name} onChange={(e) => set("parent_last_name", e.target.value)} data-testid="parent-last-name" />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Child First Name *">
                    <Input value={form.child_first_name} onChange={(e) => set("child_first_name", e.target.value)} data-testid="child-first-name" />
                  </Field>
                  <Field label="Child Last Name *">
                    <Input value={form.child_last_name} onChange={(e) => set("child_last_name", e.target.value)} data-testid="child-last-name" />
                  </Field>
                </div>
                <Field label="Child Date of Birth">
                  <Input type="date" value={form.child_dob} onChange={(e) => set("child_dob", e.target.value)} data-testid="child-dob" />
                </Field>
              </div>
            )}

            {/* Contact (always) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Email *">
                <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="waiver-email-input" />
              </Field>
              <Field label="Phone">
                <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="waiver-phone-input" />
              </Field>
            </div>
            <Field label="Address">
              <Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="waiver-address-input" />
            </Field>

            {/* Full waiver legal text — pulled from the same template used for
                emailed waivers and signed contracts so the language matches
                exactly. */}
            {legalBody && (
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4 max-h-72 overflow-y-auto" data-testid="waiver-legal-body">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Waiver Agreement</p>
                <pre className="whitespace-pre-wrap font-sans text-xs leading-relaxed text-gray-700">{legalBody}</pre>
              </div>
            )}

            {/* Signature */}
            <div className="space-y-2 pt-2 border-t border-gray-200">
              <label className="text-sm font-medium">Signature</label>
              <div className="flex gap-2">
                <Button type="button" variant={sigMode === "typed" ? "default" : "outline"} size="sm" onClick={() => setSigMode("typed")} data-testid="sig-typed-btn">
                  <Type size={14} /> Type
                </Button>
                <Button type="button" variant={sigMode === "drawn" ? "default" : "outline"} size="sm" onClick={() => setSigMode("drawn")} data-testid="sig-drawn-btn">
                  <PenTool size={14} /> Draw
                </Button>
              </div>
              {sigMode === "typed" ? (
                <div className="space-y-2">
                  <Input
                    placeholder="Type your full legal name"
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    className="text-lg font-serif italic"
                    data-testid="sig-typed-input"
                  />
                  {typedName && (
                    <div className="p-3 bg-gray-50 rounded-lg text-center text-2xl font-serif italic">{typedName}</div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
                    <canvas
                      ref={canvasRef}
                      data-testid="sig-canvas"
                      className="w-full cursor-crosshair touch-none"
                      onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
                      onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
                    />
                  </div>
                  <Button variant="ghost" size="sm" onClick={clearCanvas} data-testid="sig-clear-btn">Clear</Button>
                </div>
              )}
            </div>

            <Button
              data-testid="submit-waiver-btn"
              className="w-full"
              onClick={submit}
              disabled={submitting}
            >
              {submitting ? "Submitting..." : "Sign Waiver"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-gray-600">{label}</label>
      {children}
    </div>
  );
}
